import React, { createContext, FC, PropsWithChildren, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthResponse, LocationOption, OrganizationOption } from 'Src/feature/provision/provisionService';
import { logOut } from 'Src/network/socket';

enum Step {
  Login,
  LocationSelect,
  TerminalSelect,
  Review,
}

const useEmailWorkFlowStore = () => {
  const { t } = useTranslation();

  const [data, dataSet] = useState({
    locations: [] as LocationOption[],
    organizations: [] as OrganizationOption[],
    locationId: null as number | null,
    organizationId: null as number | null,
    terminalId: null as number | null,
    user: undefined as AuthResponse['bridgeUser'] | undefined,
  });

  const orderedSteps = [Step.Login, Step.LocationSelect, Step.TerminalSelect, Step.Review];
  const [currentStep, currentStepSet] = useState(orderedSteps[0]);
  const [canGoNext, canGoNextSet] = useState(false);
  const [prevLoading, prevLoadingSet] = useState(false);
  const [nextLoading, nextLoadingSet] = useState(false);


  const dataRef = useRef(data);
  dataRef.current = data;

  const updateData = (updateData: Partial<typeof data>) => {
    dataRef.current = {
      ...dataRef.current,
      ...updateData,
    };
    dataSet(dataRef.current);
  };

  const goPrev = () => {
    const evtName: keyof HTMLElementEventMap = 'emailWorkflow.onGoPrev';
    const evt = new CustomEvent(evtName, { cancelable: true });
    prevLoadingSet(true);
    document.dispatchEvent(evt);
    if (evt.defaultPrevented) {
      prevLoadingSet(false);
      return;
    }
    const idx = orderedSteps.indexOf(currentStep);
    const prev = orderedSteps[idx - 1];
    if (prev >= Step.Login) {
      switch (prev) {
        case Step.LocationSelect:
          updateData({
            terminalId: null,
          });
          break;
        case Step.Login:
          void logOut();
          updateData({
            organizationId: null,
            locationId: null,
          });
      }
      currentStepSet(prev);
    }
    prevLoadingSet(false);
  };

  const goNext = () => {
    const evtName: keyof HTMLElementEventMap = 'emailWorkflow.onGoNext';
    const evt = new CustomEvent(evtName, { cancelable: true });
    nextLoadingSet(true);
    document.dispatchEvent(evt);
    if (evt.defaultPrevented) {
      nextLoadingSet(false);
      return;
    }
    const idx = orderedSteps.indexOf(currentStep);
    const next = orderedSteps[idx + 1];
    if (next) {
      currentStepSet(next);
    }
    nextLoadingSet(false);
  };

  const currentOrganization = data.organizationId
    ? data.organizations.find(({ id }) => id === data.organizationId)
    : undefined;

  const currentLocation = data.locationId
    ? data.locations.find(({ id }) => id === data.locationId)
    : undefined;

  const currentTerminal = data.terminalId
    ? currentLocation?.terminals.find(({ id }) => id === data.terminalId)
    : undefined;

  return {
    data: {
      ...data,
      current: {
        organization: currentOrganization,
        location: currentLocation,
        terminal: currentTerminal,
      }
    },
    updateData,
    steps: {
      labels: {
        [Step.Login]: t('provision.emailWorkFlow.labels.signIn'),
        [Step.LocationSelect]: t('provision.emailWorkFlow.labels.location'),
        [Step.TerminalSelect]: t('provision.emailWorkFlow.labels.device'),
        [Step.Review]: t('provision.emailWorkFlow.labels.finish'),
      },
      goPrev,
      goNext,
      get current() {
        return currentStep;
      },
      set current(next:Step) {
        currentStepSet(next);
      },
      get canGoPrev() {
        return currentStep != Step.Login;
      },
      get canGoNext() {
        return canGoNext;
      },
      set canGoNext(next) {
        canGoNextSet(next);
      },
      get isFinal() {
        return currentStep === Step.Review;
      },
      get isFirst() {
        return currentStep === Step.Login;
      },
      set nextLoading(loading: boolean) {
        nextLoadingSet(loading);
      },
      get nextLoading() {
        return nextLoading;
      },
      set prevLoading(loading: boolean) {
        prevLoadingSet(loading);
      },
      get prevLoading() {
        return prevLoading;
      },
    },
  };
};

type UsedEmailWorkFlowStore = ReturnType<typeof useEmailWorkFlowStore>;

const EmailWorkFlowStoreContext = createContext<UsedEmailWorkFlowStore | undefined>(undefined);
EmailWorkFlowStoreContext.displayName = 'EmailWorkFlowStoreContext';

const EmailWorkFlowStoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const store = useEmailWorkFlowStore();
  return (
    <EmailWorkFlowStoreContext.Provider value={store}>
      {children}
    </EmailWorkFlowStoreContext.Provider>
  );
};

const useEmailWorkflow = () => {
  const store = useContext(EmailWorkFlowStoreContext);
  if (!store) throw new Error('useTokenWorkflow() must be used within a EmailWorkFlowStoreProvider');
  return store;
};


export { EmailWorkFlowStoreProvider, Step, useEmailWorkflow };
